import { MetaHandler } from "@/main/common/MetaHandler";

// FI-5280 (settle ⚑G-32 option (b), r186q): the server's first-touch content-locale inference
// (`FirstTouchContentLocaleInterceptor`) renders an anonymous pt/es landing in the content's language but no longer
// writes the locale cookie, because a response carrying a `Set-Cookie` is never edge-cached (`EdgeCacheableRender`).
// This module persists that inferred locale client-side instead, so the visitor's NEXT request (a non-content page such
// as `/`) still resolves the same chrome. It writes Spring's `CookieLocaleResolver` cookie with the page's `<html lang>`
// only when the page says its chrome was inferred (`seavien-locale-signal` = CONTENT_CANONICAL or CONTENT_PARAM). An
// explicit `?lang=` choice and a signed-in viewer never carry those signals, so their server-written cookie is
// untouched. Session cookie (no Max-Age), like the server's. Called once per HARD load from `bootstrapFrontEnd`.
// Mechanism: designs/04-live/2026-06-03-anonymous-first-touch-content-locale-inference-design.md "Behavior" step 2.

/** Spring's `CookieLocaleResolver.DEFAULT_COOKIE_NAME`, the name the CloudFront cache key also reads. */
export const LOCALE_COOKIE_NAME = "org.springframework.web.servlet.i18n.CookieLocaleResolver.LOCALE";

const INFERRED_LOCALE_SIGNALS: ReadonlySet<string> = new Set(["content_canonical", "content_param"]);
const LANGUAGE_TAG_PATTERN = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;

let persisted = false;

export function persistInferredContentLocale(): void {
    if (persisted || typeof window === "undefined" || typeof document === "undefined") {
        return;
    }
    persisted = true;
    const signal = MetaHandler.getLocaleSignal();
    if (signal === undefined || !INFERRED_LOCALE_SIGNALS.has(signal)) {
        return;
    }
    if (MetaHandler.isUserLoggedIn()) {
        return;
    }
    const languageTag = (document.documentElement.getAttribute("lang") ?? "").trim();
    if (!LANGUAGE_TAG_PATTERN.test(languageTag)) {
        return;
    }
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${LOCALE_COOKIE_NAME}=${languageTag}; Path=/; SameSite=Lax${secure}`;
}

export function resetInferredContentLocaleCookieForTests(): void {
    persisted = false;
}
